import React from "react";
import { Input } from "antd";
import type { GetProps, GetRef } from "antd";
import useLPDraft from "../useLPDraft";

type TextAreaRef = GetRef<typeof Input.TextArea>;
type TextAreaProps = GetProps<typeof Input.TextArea>;

const LPTextArea = React.memo(
  React.forwardRef<TextAreaRef, TextAreaProps>(({ value, onChange, onBlur, onPressEnter, ...rest }, ref) => {
    const { draft, change, commit } = useLPDraft(value == null ? "" : String(value));
    const inputRef = React.useRef<TextAreaRef>(null);
    const pendingChangeEventRef = React.useRef<React.ChangeEvent<HTMLTextAreaElement>>();

    React.useImperativeHandle(ref, () => inputRef.current as TextAreaRef);

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      pendingChangeEventRef.current = event;
      change(event.target.value);
    };

    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      commit(() => {
        const pendingChangeEvent = pendingChangeEventRef.current;
        if (pendingChangeEvent) {
          onChange?.(pendingChangeEvent);
        }
      });
      pendingChangeEventRef.current = undefined;
      onBlur?.(event);
    };

    const handlePressEnter = React.useCallback(
      (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        onPressEnter?.(event);

        if (event.defaultPrevented || event.shiftKey || event.nativeEvent.isComposing) {
          return;
        }

        event.preventDefault();
        inputRef.current?.blur();
      },
      [onPressEnter],
    );

    return (
      <Input.TextArea {...rest} ref={inputRef} value={draft} onBlur={handleBlur} onChange={handleChange} onPressEnter={handlePressEnter} />
    );
  }),
);

LPTextArea.displayName = "LPTextArea";

export default LPTextArea;
